import formbricks from '@formbricks/js';
import { useEffect, useRef } from 'react';
import { getTenant } from '../../tenant-support/get-tenant';
import { useAuthenticatedUser } from '../../authentication/auth-wrapper';
import { moduleClientFactory } from '../../api-service/module-client-factory';
import { win } from '../../utilities/window';
import { useTenantConfiguration } from '../../hooks/use-tenant-configuration';
import { useRoleThemes } from '../../authentication/use-role-themes';

const FORMBRICKS_PROD_WORKSPACE_ID = 'cmnd9fcjz0ui2px01r7ei556k';
const FORMBRICKS_NON_PROD_WORKSPACE_ID = 'cmlf74dar01mv0c018brbsy1v';

function getFormbricksWorkspaceId(): string {
  const { hostname } = win.location;
  const isProd = hostname.endsWith('.schultzfasit.dk') && !hostname.match(/\.f\d\.schultzfasit\.dk$/);
  return isProd ? FORMBRICKS_PROD_WORKSPACE_ID : FORMBRICKS_NON_PROD_WORKSPACE_ID;
}

const FORMBRICKS_USER_ID_KEY = 'formbricks-anonymous-id';

const configurationClient = moduleClientFactory.createConfigurationClient();

function getAnonymousUserId(): string {
  const stored = localStorage.getItem(FORMBRICKS_USER_ID_KEY);
  if (stored) return stored;

  const id = crypto.randomUUID();
  localStorage.setItem(FORMBRICKS_USER_ID_KEY, id);
  return id;
}

function jaEllerNej(value: boolean): string {
  return value ? 'ja' : 'nej';
}

export function FormbricksSurvey() {
  const user = useAuthenticatedUser();
  const { formbricksConfiguration } = useTenantConfiguration();
  const { isAuthority, isServiceProvider, rolesLoaded } = useRoleThemes();
  const initializing = useRef(false);

  useEffect(() => {
    if (initializing.current) return;
    if (!user?.profile?.sub) return;
    if (!formbricksConfiguration || formbricksConfiguration.disabled) return;
    if (!rolesLoaded) return;

    initializing.current = true;

    async function initialize() {
      try {
        const { disabled } = await configurationClient.getFormbricksSystemWideDisableStatus();
        if (disabled) return;

        const currentUserId = user?.profile.id;
        const excludedUsers = (formbricksConfiguration?.excludedUsers ?? []).map(id => id.toLowerCase());
        if (currentUserId && excludedUsers.includes(currentUserId.toLowerCase())) return;

        const tenant = getTenant();

        await formbricks.setup({
          workspaceId: getFormbricksWorkspaceId(),
          appUrl: 'https://app.formbricks.com',
        });

        // Any await between setup and setUserId lets a survey render against an anonymous contact.
        await formbricks.setUserId(getAnonymousUserId());
        await formbricks.setAttributes({
          tenant: tenant.name,
          er_myndighed: jaEllerNej(isAuthority),
          er_udfoerer: jaEllerNej(isServiceProvider),
        });
      } catch (error) {
        initializing.current = false;
        console.warn('Formbricks initialization error:', error);
      }
    }

    initialize();
  }, [user, formbricksConfiguration, rolesLoaded, isAuthority, isServiceProvider]);

  return null;
}
